import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';

import { Box, SxProps, Theme } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import seedrandom from 'seedrandom';

import { ROILineChartHeader } from './ROILineChartHeader';

const ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;

/**
 * This function makes a squiggly line by interpolating between the points in the data array.
 * It then applies a sine wave to the line to make it squiggly.
 * The sine wave is determined by the index of the point in the data array.
 *
 * Essentially, we are adding noise to the line to make it more interesting.
 */
function makeSquigglyLine(
  data: number[],
  resolution: number,
  amplitude: number,
  schoolId: string | number,
): number[] {
  const rng = seedrandom(schoolId.toString());

  return data
    .slice(0, -1)
    .flatMap((start, i) => {
      const end = data[i + 1];

      return Array.from({ length: resolution }, (_, j) => {
        const t = j / resolution;
        const linear = (1 - t) * start + t * end;

        const freqMultiplier = 4 + Math.floor(rng() * 6);
        const ampMultiplier = 0.7 + rng() * 1.0;
        const phaseOffset = rng() * Math.PI * 2;

        const wiggle =
          Math.sin(t * Math.PI * freqMultiplier + phaseOffset) *
          amplitude *
          ampMultiplier *
          (end - start);

        return linear + wiggle;
      });
    })
    .concat(data[data.length - 1]);
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

type ROILineChartProps = Readonly<{
  width?: number;
  height?: number;
  actualData: number[];
  title: string;
  total_roi: number;
  /** used for generating a unique seed for the squiggly line */
  schoolId: string | number;
}>;

export const ROILineChart = ({ actualData, title, total_roi, schoolId }: ROILineChartProps) => {
  const resolution = 2;
  const howTallOfSquiggles = 0.25;

  const squigglyData = makeSquigglyLine(actualData, resolution, howTallOfSquiggles, schoolId);

  const squigglyLabels = Array.from({ length: squigglyData.length }, (_, i) =>
    (i / resolution).toFixed(1),
  );

  const data = {
    labels: squigglyLabels,
    datasets: [
      {
        label: title,
        data: squigglyData,
        borderColor: COLOR_TOKENS['Brand/500'],
        tension: 0.25,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const lowestTick = Math.min(...actualData);
  const highestTick = Math.max(...actualData);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        title: {
          display: true,
          text: ROI_LINE_CHART_STRINGS.yAxisLabel,
          color: COLOR_TOKENS['Colors/Text/text-tertiary'],
          font: {
            size: 12,
            family: 'Sarabun, sans-serif',
          },
        },
        grid: {
          tickColor: 'transparent',
          display: true,
          color: (context) => {
            const value = context.tick.value;

            if (value === 0) {
              return COLOR_TOKENS['Colors/Foreground/fg-tertiary'];
            }

            return COLOR_TOKENS['Colors/Border/border-tertiary'];
          },
        },
        border: {
          display: false,
        },
        min: Math.min(lowestTick - 100000, -250000),
        max: Math.max(highestTick + 50000, 500000),
        afterBuildTicks: (axis) => {
          // we will always have at least -250 -> 1m, but we will increase/decrease in 250k increments to the highest/lowest tick
          const minTick = Math.min(Math.floor(lowestTick / 250000) * 250000, -250000);
          const maxTick = Math.max(Math.ceil(highestTick / 250000) * 250000, 1000000);

          const tickCount = (maxTick - minTick) / 250000 + 1;

          axis.ticks = Array.from({ length: tickCount }, (_, i) => ({
            value: minTick + i * 250000,
          }));
        },
        ticks: {
          callback: (value) => {
            const valueAsNumber = Number(value);

            // format the tick like $250k, $500k, $750k, $1m, $1.25m, $1.5m, $1.75m, $2m
            if (valueAsNumber % 250000 === 0) {
              const absValue = Math.abs(valueAsNumber);
              const suffix = absValue >= 1000000 ? 'M' : 'K';
              const formattedValue = absValue >= 1000000 ? absValue / 1000000 : absValue / 1000;

              return `${valueAsNumber < 0 ? '-' : ''}$${formattedValue}${suffix} `;
            }

            return '';
          },
          font: {
            size: 12,
            weight: 'lighter',
            family: 'Sarabun, sans-serif',
          },
        },
      },
      x: {
        title: {
          display: true,
          text: ROI_LINE_CHART_STRINGS.xAxisLabel,
          color: COLOR_TOKENS['Colors/Text/text-tertiary'],
          padding: {
            top: 16,
            bottom: 0,
          },
          font: {
            size: 12,
            family: 'Sarabun, sans-serif',
          },
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          align: 'end',
          font: {
            size: 12,
            weight: 'lighter',
            family: 'Sarabun, sans-serif',
          },

          autoSkip: false,
          maxTicksLimit: squigglyLabels.length,
          minRotation: 0,
          maxRotation: 0,

          callback: (_value, index) => {
            const label = Number(squigglyLabels[index]);

            if (Number.isInteger(label) && label % 2 === 0 && label !== 0) {
              return label.toString();
            }

            return '';
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    plugins: {
      tooltip: {
        enabled: true,
        displayColors: false,
        padding: {
          top: 8,
          bottom: 8,
          left: 12,
          right: 12,
        },
        caretSize: 0,
        yAlign: 'bottom',
        callbacks: {
          title: function (context) {
            const hoveredIndex = context[0].dataIndex;
            const year = Math.round(hoveredIndex / resolution); // round to nearest real year

            return `${ROI_LINE_CHART_STRINGS.xAxisLabel} ${year}`;
          },
          label: function (context) {
            const year = Math.round(context.dataIndex / resolution);
            const label = context.dataset.label || '';
            const originalValue = actualData[year];

            return `${label}: ${formatCurrency(originalValue)}`;
          },
        },
      },
      legend: {
        labels: {
          generateLabels: () => [],
        },
      },
      title: {
        display: false,
      },
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
  };

  return (
    <Box sx={containerSx}>
      <ROILineChartHeader total_roi={total_roi} />

      <Box sx={chartContainerSx}>
        <Line data={data} options={options} />
      </Box>
    </Box>
  );
};

const chartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '100%',
  mt: backrs.spacing['spacing-xl'],
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 768px)': {
    width: '100%',
  },
  '@media (max-width: 400px)': {
    width: '100%',
  },
  height: { xs: 'auto', lg: '405px' },
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  borderRadius: backrs.radius['radius-xl'],
  padding: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
});
